import { useEffect, useRef, type JSX } from "react";
import { Button } from "../../../components";
import type { ReviewStepEvent } from "../../../data/chatQuery";
import { DecidedReviewCard } from "./DecidedReviewCard";
import { ReviewCard, reviewCompactEnabled } from "./ReviewCard";

// [after-review-inline] The inline review model: clause cards live IN the chat
// stream, not a side pane. Decided clauses collapse to one-line receipt rows;
// the clause under review is the single open card. This is a SECOND RENDERING of
// the exact same ReviewStep state + deterministic decide/goto the side pane uses
// (ReviewPane) — no new backend. Reopening a receipt calls onGoto, which the
// backend answers with that clause as `current`, so it becomes the open card.
//
// Default ON. Flip back to the classic side pane by setting localStorage
// `cs.reviewInline` = "false" — the pane code is kept, just hidden, so we can
// switch back without a rebuild.
export function reviewInlineEnabled(): boolean {
  try {
    return window.localStorage.getItem("cs.reviewInline") !== "false";
  } catch {
    return true;
  }
}

type Step = ReviewStepEvent["steps"][number];

function receiptText(decision: Step["decision"]): string {
  switch (decision) {
    case "applied":
      return "Change applied";
    case "kept":
      return "Kept as drafted — logged as a deviation";
    case "skipped":
      return "Left to decide later — still on the agenda";
    default:
      return "";
  }
}

// A decided clause, collapsed to a clickable one-line receipt. Clicking reopens
// it (onGoto) — the backend returns it as the current clause and it expands.
function Receipt({
  step,
  pending,
  onOpen,
}: {
  step: Step;
  pending: boolean;
  onOpen: () => void;
}): JSX.Element {
  const state =
    step.decision === "applied"
      ? "applied"
      : step.decision === "kept"
        ? "kept"
        : "skipped";
  const label =
    step.decision === "applied"
      ? "Applied"
      : step.decision === "kept"
        ? "Kept"
        : "Later";
  return (
    <Button
      variant="bare"
      className={`sa-rvi__receipt is-${state}`}
      disabled={pending}
      onClick={onOpen}
      title="Reopen this clause"
    >
      <span className={`sa-rvi__receipt-dot is-${state}`} aria-hidden="true" />
      <span className="sa-rvi__receipt-name">{step.name}</span>
      <span className="sa-rvi__receipt-txt">{receiptText(step.decision)}</span>
      <span className={`sa-rvi__receipt-pill is-${state}`}>{label}</span>
    </Button>
  );
}

// [after-review-inline] Renders the whole review as a stream of receipts + one
// open card + a closing summary, from the same ReviewStep the side pane consumes.
export function InlineReview({
  review,
  pending,
  onDecide,
  onGoto,
  onTypeSubmit,
  onResolveSkips,
  onJumpToDraft,
  revising = false,
  revealNext = false,
}: {
  review: ReviewStepEvent;
  pending: boolean;
  onDecide: (index: number, decision: "apply" | "keep" | "skip" | "undo") => void;
  onGoto: (index: number) => void;
  onTypeSubmit: (index: number, name: string, text: string) => void;
  revising?: boolean;
  // [review-compact] The user has acted on this review; scroll each next card in.
  revealNext?: boolean;
  onResolveSkips: (choice: "review" | "finish") => void;
  // [Fix 4] Reveal + scroll the rail draft to the clause under review.
  onJumpToDraft?: () => void;
}): JSX.Element {
  const { steps, currentIndex, current, counts, total, status } = review;
  const compact = reviewCompactEnabled();
  // [review-compact] After the user acts, bring the next clause's card into view.
  // Done here (the stable container) rather than in the card, which remounts as
  // the thread re-renders around it and would cancel its own scroll.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!compact || !revealNext) return;
    // A short timer (not rAF, which a background tab never fires) lets the thread
    // finish laying out around the new card first.
    const t = window.setTimeout(() => {
      // The open card, or (when nothing is open) the skip prompt / completion summary.
      const found = rootRef.current?.querySelectorAll(
        "[data-testid=review-card], .sa-rvi__prompt, [data-testid=review-summary]"
      );
      const el = found !== undefined && found.length > 0 ? found[found.length - 1] : null;
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
    }, 60);
    return () => window.clearTimeout(t);
  }, [compact, revealNext, currentIndex]);
  const missingClauses = review.missingClauses ?? [];
  const noActionCount = review.noActionCount ?? 0;
  const awaitingSkipReview = review.awaitingSkipReview === true;

  // [async-review] The plan is still computing in the background — show a live
  // placeholder (the session-detail query polls until it flips to the first
  // clause). No clauses/counts exist yet, so short-circuit the normal card body.
  if (status === "building") {
    return (
      <div
        className="sa-rvi"
        aria-label="Clause-by-clause review (building)"
        aria-busy="true"
        data-testid="inline-review-building"
      >
        <div className="sa-rvi__building">
          <span className="sa-rvi__building-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span>Reviewing your draft — checking each clause against the certified standard…</span>
        </div>
      </div>
    );
  }
  // [async-review] The background build failed (no matching standard, or an
  // error). Terminal — the pane stops polling; the user can ask again to retry.
  if (status === "failed") {
    return (
      <div
        className="sa-rvi"
        aria-label="Clause-by-clause review (failed)"
        data-testid="inline-review-failed"
      >
        <div className="sa-rvi__building">
          <span>
            {review.error ??
              "The review couldn't be built. Ask me to review it again."}
          </span>
        </div>
      </div>
    );
  }
  // [walkthrough-actionable] Only clauses that need a decision are carded; `total`
  // already counts just those. High-risk among them leads the triage line.
  const highRisk = steps.filter((s) => s.needsReview && s.risk === "high").length;

  return (
    <div
      ref={rootRef}
      className="sa-rvi"
      aria-label="Clause-by-clause review (inline)"
      data-testid="inline-review"
    >
      {/* Triage + running counts — the classic inline header. The compact card
          carries its own progress row, so this row is hidden then. */}
      {!reviewCompactEnabled() && (
      <div className="sa-rvi__head">
        <span className="sa-rvi__triage" data-testid="review-triage">
          {total} to review
          {highRisk > 0 ? (
            <>
              {" · "}
              <b className="sa-rvi__triage-high">{highRisk} high-risk</b>
            </>
          ) : null}
        </span>
        <span className="sa-rvi__counts">
          <span className="sa-rvi__count">
            <i className="sa-rvi__count-dot is-applied" aria-hidden="true" />
            {counts.applied}
          </span>
          <span className="sa-rvi__count">
            <i className="sa-rvi__count-dot is-kept" aria-hidden="true" />
            {counts.kept}
          </span>
          <span className="sa-rvi__count">{counts.left} left</span>
        </span>
      </div>
      )}

      {/* [completeness-first] Standard clauses the draft is MISSING — the other
          half of a completeness review (the draft may also be INCOMPLETE, not
          just deviating). Informational: "consider adding". */}
      {missingClauses.length > 0 && (
        <div className="sa-rvi__missing" data-testid="review-missing">
          <div className="sa-rvi__missing-head">
            {missingClauses.length} standard clause
            {missingClauses.length === 1 ? "" : "s"} not in your draft — each is a
            step below to add or leave out
          </div>
          <ul className="sa-rvi__missing-list">
            {missingClauses.map((m, i) => (
              <li
                key={`${m.name}-${i}`}
                className="sa-rvi__missing-item"
                title={m.text}
              >
                {m.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* [walkthrough-actionable] Clauses that need no action (compliant with the
          standard, or additional with no conflict) — summarized, not carded. */}
      {noActionCount > 0 && (
        <div className="sa-rvi__noaction" data-testid="review-noaction">
          {noActionCount} clause{noActionCount === 1 ? "" : "s"} match the standard
          or add terms it doesn&rsquo;t cover — no action needed.
        </div>
      )}

      {steps.map((s) => {
        // [walkthrough-actionable] Only clauses that need a decision are carded;
        // compliant / clean-additional clauses (auto-decided) are summarized above.
        if (!s.needsReview) return null;
        // The clause under review → the single open card (full detail from
        // `current`). Decided clauses (not the open one) → receipts. Future
        // pending clauses aren't surfaced yet — they open when reached.
        if (
          s.index === currentIndex &&
          current !== null &&
          !awaitingSkipReview
        ) {
          return (
            <div
              key={s.index}
              className={`sa-rvi__open${compact ? " is-compact sa-msg sa-msg--assistant" : ""}`}
            >
              <ReviewCard
                current={current}
                walked={steps.filter((s) => s.needsReview).map((s) => s.index)}
                pending={pending}
                decision={s.decision}
                onDecide={onDecide}
                onGoto={onGoto}
                onTypeSubmit={onTypeSubmit}
                revising={revising}
                {...(onJumpToDraft ? { onJumpToDraft } : {})}
                progress={{ steps, counts, total }}
                hideNav
              />
            </div>
          );
        }
        if (s.decision !== "pending") {
          // [review-compact] Decided clauses stay in the thread as collapsed cards.
          if (compact) {
            return (
              <DecidedReviewCard
                key={s.index}
                step={s}
                pending={pending}
                onOpen={() => onGoto(s.index)}
              />
            );
          }
          return (
            <Receipt
              key={s.index}
              step={s}
              pending={pending}
              onOpen={() => onGoto(s.index)}
            />
          );
        }
        return null;
      })}

      {awaitingSkipReview && (
        <div className="sa-rvi__prompt">
          <div className="sa-rvi__prompt-txt">
            You&rsquo;ve reviewed everything except {counts.skipped}{" "}
            clause{counts.skipped === 1 ? "" : "s"} you left to decide later.
          </div>
          <div className="sa-rvi__prompt-acts">
            <Button
              variant="bare"
              className="sa-rev__btn sa-rev__btn--apply"
              disabled={pending}
              onClick={() => onResolveSkips("review")}
            >
              Review skipped
            </Button>
            <Button
              variant="bare"
              className="sa-rev__btn sa-rev__btn--keep"
              disabled={pending}
              onClick={() => onResolveSkips("finish")}
            >
              Finish review
            </Button>
          </div>
        </div>
      )}

      {status === "complete" && (
        <div className="sa-rvi__summary" data-testid="review-summary">
          {total === 0 ? (
            // Nothing needed a decision: say so, instead of "All 0 clauses … are
            // decided" with a row of zeros.
            <>
              <div className="sa-rvi__summary-title">Nothing needs your decision</div>
              <div className="sa-rvi__summary-body">
                No deviations or conflicts to resolve.
                {missingClauses.length > 0 &&
                  " The standard clauses listed above aren’t in your draft."}
              </div>
            </>
          ) : (
            <>
              <div className="sa-rvi__summary-title">
                {total === 1
                  ? "The 1 clause that needed review is decided"
                  : `All ${total} clauses that needed review are decided`}
              </div>
              <div className="sa-rvi__summary-body">
                {counts.applied} applied, {counts.kept} kept as drafted,{" "}
                {counts.skipped} skipped.
                {/* [Fix 4] Applied clauses were written to the draft as each was
                    approved — no end-of-review "create draft" step. */}
                {counts.applied > 0 &&
                  " Approved changes are in your draft."}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
